<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Sticky bottom bar (verschijnt als add-to-cart buiten beeld is) ── -->
    <transition name="slide-up">
      <div
        v-if="showStickyBar && product"
        class="fixed bottom-0 left-0 right-0 z-[900] bg-white border-t border-[#e9eaec] shadow-[0_-4px_20px_rgba(0,0,0,0.09)]"
      >
        <div class="max-w-screen-xl mx-auto px-4 py-3 flex items-center gap-3">
          <!-- Thumbnail -->
          <img
            v-if="currentImage"
            :src="sized(currentImage, 96)"
            :alt="product.name"
            class="w-11 h-11 object-contain rounded-lg border border-[#e9eaec] bg-white p-1 shrink-0"
          >
          <!-- Naam + prijs -->
          <div class="flex-1 min-w-0">
            <p class="text-[0.82rem] font-semibold text-[#111] truncate leading-tight">{{ product.name }}</p>
            <div class="flex items-baseline gap-2 mt-0.5">
              <span v-if="product.on_sale" class="text-[0.72rem] text-[#bbb] line-through">{{ formatPrice(product.regular_price) }}</span>
              <span class="text-[1rem] font-extrabold text-[#111] leading-none">{{ formatPrice(currentPrice) }}</span>
              <span class="text-[0.7rem] text-[#aaa]">incl. btw</span>
            </div>
          </div>
          <!-- Knoppen -->
          <button
            v-if="isBundle || isGrouped"
            @click="$el.querySelector('.bundle-section, .grouped-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })"
            class="flex items-center gap-2 bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-bold text-[0.875rem] px-4 py-2.5 rounded-xl transition-all shrink-0"
          >
            <i class="bi bi-box-seam"></i>
            <span class="hidden sm:inline">Bekijk set</span>
          </button>
          <button
            v-else
            @click="addToCart"
            :disabled="product.stock_status === 'outofstock' || adding"
            class="flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold text-[0.875rem] px-4 py-2.5 rounded-xl transition-all shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i :class="['bi', adding ? 'bi-arrow-repeat' : 'bi-cart-plus']"></i>
            <span>In winkelwagen</span>
          </button>
          <button
            @click="toggleWishlist"
            :class="['w-10 h-10 hidden sm:flex items-center justify-center rounded-xl border text-[1rem] transition-all shrink-0',
              inWishlist ? 'bg-[#fffbeb] border-[#FFD500] text-[#FFD500]' : 'border-[#e5e7eb] text-[#bbb] hover:border-[#FFD500] hover:text-[#FFD500]']"
          >
            <i :class="['bi', inWishlist ? 'bi-heart-fill' : 'bi-heart']"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- Loading - skeleton spiegelt de detail-layout zodat er geen layout shift is -->
    <div v-if="loading" class="bg-white border-b border-[#e9eaec]" aria-hidden="true">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
        <!-- Breadcrumb -->
        <div class="h-3 w-56 bg-gray-100 rounded animate-pulse mb-6"></div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          <!-- Links: galerij -->
          <div class="flex gap-3 items-start">
            <div class="hidden lg:flex flex-col gap-2 shrink-0">
              <div v-for="n in 4" :key="n" class="w-[66px] h-[66px] rounded-lg bg-gray-100 animate-pulse"></div>
            </div>
            <div class="flex-1 aspect-square rounded-xl bg-gray-100 animate-pulse"></div>
          </div>
          <!-- Rechts: productinfo -->
          <div>
            <div class="h-3 w-24 bg-gray-100 rounded animate-pulse mb-4"></div>
            <div class="h-7 w-11/12 bg-gray-100 rounded animate-pulse mb-2.5"></div>
            <div class="h-7 w-2/3 bg-gray-100 rounded animate-pulse mb-6"></div>
            <div class="h-4 w-40 bg-gray-100 rounded animate-pulse mb-6"></div>
            <div class="h-10 w-48 bg-gray-100 rounded animate-pulse mb-3"></div>
            <div class="h-3.5 w-32 bg-gray-100 rounded animate-pulse mb-8"></div>
            <div class="flex gap-3 mb-6">
              <div class="h-12 flex-1 bg-gray-100 rounded-xl animate-pulse"></div>
              <div class="h-12 w-14 bg-gray-100 rounded-xl animate-pulse"></div>
            </div>
            <div class="h-4 w-full bg-gray-100 rounded animate-pulse mb-2"></div>
            <div class="h-4 w-5/6 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="!product" class="max-w-screen-xl mx-auto px-6 py-20 text-center">
      <i class="bi bi-exclamation-triangle text-[3rem] text-[#ddd] mb-4 block"></i>
      <h2 class="text-[1.5rem] font-bold text-[#111] mb-2">Product niet gevonden</h2>
      <p class="text-[#888] mb-6">Het product dat je zoekt bestaat niet of is verwijderd.</p>
      <router-link to="/producten" class="inline-flex items-center gap-2 bg-[#FFD500] text-[#111] font-bold px-6 py-3 rounded-xl no-underline">
        <i class="bi bi-arrow-left"></i> Terug naar producten
      </router-link>
    </div>

    <!-- Product content -->
    <div v-else>

      <!-- Product hoofdsectie: full-width wit vlak (steigro-stijl) -->
      <div class="bg-white border-b border-[#e9eaec]">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">

      <!-- Breadcrumb -->
      <nav class="flex items-center gap-1.5 text-[0.78rem] text-[#999] mb-6 flex-wrap">
        <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
        <i class="bi bi-chevron-right text-[0.6rem]"></i>
        <router-link to="/producten" class="hover:text-[#111] transition-colors no-underline">Producten</router-link>
        <template v-if="productCategory && productCategorySlug">
          <i class="bi bi-chevron-right text-[0.6rem]"></i>
          <router-link :to="`/categorie/${productCategorySlug}`" class="hover:text-[#111] transition-colors no-underline">{{ productCategory }}</router-link>
        </template>
        <i class="bi bi-chevron-right text-[0.6rem]"></i>
        <span class="text-[#555] font-medium truncate max-w-[200px]">{{ product.name }}</span>
      </nav>

      <!-- Subtiele meta-rij: like + countdown -->
      <div v-if="likeCount > 0 || (product.on_sale && dealTimeLeft)" class="mb-5 flex flex-wrap items-center gap-2">
        <button
          v-if="likeCount > 0"
          @click="toggleLike"
          :class="['flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.78rem] font-semibold border transition-all',
            liked
              ? 'bg-red-50 border-red-200 text-red-500'
              : 'border-[#e9eaec] text-[#999] hover:border-red-200 hover:text-red-400']"
        >
          <i :class="['bi text-[0.8rem] transition-transform', liked ? 'bi-heart-fill text-red-500' : 'bi-heart']"></i>
          {{ likeCount }} mensen vinden dit interessant
        </button>
        <div v-if="product.on_sale && dealTimeLeft" class="flex items-center gap-2 bg-[#fffbeb] border border-[#FFD500]/50 rounded-full pl-3 pr-4 py-1 text-[0.78rem] font-semibold text-[#7a5f00]">
          <i class="bi bi-alarm text-[#c8a000] text-[0.8rem]"></i>
          Actie eindigt over <strong>{{ dealTimeLeft }}</strong>
        </div>
      </div>

      <!-- Main grid: images + product info - two columns on desktop, stacked on mobile -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 mb-8 items-start">

        <!-- ── Left: Image gallery ── -->
        <div class="flex gap-3 items-start">

          <!-- Verticale thumbnailstrip (alleen desktop) -->
          <div v-if="product.images && product.images.length > 1" class="hidden lg:flex flex-col gap-2 shrink-0">
            <button
              v-for="(img, i) in product.images.slice(0, 6)"
              :key="i"
              @click="currentImage = img.src"
              :class="['w-[66px] h-[66px] rounded-lg overflow-hidden bg-white flex items-center justify-center p-1.5 transition-all border-2',
                currentImage === img.src ? 'border-[#FFD500]' : 'border-transparent hover:border-[#e0e0e0]']"
            >
              <img :src="sized(img.src, 130)" :alt="`${product.name} ${i+1}`" loading="lazy" width="66" height="66" class="w-full h-full object-contain">
            </button>
          </div>

          <!-- Hoofdafbeelding + mobiele thumbnails -->
          <div class="flex-1 min-w-0">
            <div
              class="relative bg-white rounded-xl overflow-hidden cursor-zoom-in group"
              style="aspect-ratio:1/1"
              @click="lightboxOpen = true"
              title="Klik om te vergroten"
            >
              <!-- Sale badge -->
              <div v-if="product.on_sale && savingsPercentage > 0" class="absolute top-4 left-4 z-10 flex flex-col gap-1">
                <span class="bg-red-500 text-white text-[0.72rem] font-bold px-2.5 py-1 rounded-lg">-{{ savingsPercentage }}%</span>
              </div>
              <!-- Free shipping badge -->
              <div v-if="parseFloat(currentPrice) >= 50" class="absolute top-4 right-4 z-10 flex items-center gap-1 bg-green-500 text-white text-[0.68rem] font-bold px-2.5 py-1 rounded-lg">
                <i class="bi bi-truck text-[0.75rem]"></i> Gratis verzending
              </div>
              <!-- Zoom hint -->
              <div class="absolute bottom-3 right-3 z-10 flex items-center gap-1 bg-black/40 text-white text-[0.65rem] font-semibold px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <i class="bi bi-zoom-in text-[0.7rem]"></i> Vergroten
              </div>
              <!-- LQIP: geblurde 32px-placeholder, fade weg als de scherpe foto er is -->
              <img
                v-if="lqipSrc"
                :src="lqipSrc"
                alt="" aria-hidden="true"
                class="absolute inset-0 w-full h-full object-contain p-8 transition-opacity duration-500"
                :class="mainImgLoaded ? 'opacity-0' : 'opacity-100'"
                style="filter: blur(16px); transform: scale(1.06);"
              >
              <img
                ref="mainImgEl"
                :src="currentImage ? sized(currentImage, 720) : '/placeholder-product.jpg'"
                :alt="product.name"
                width="720" height="720" fetchpriority="high" decoding="async"
                class="absolute inset-0 w-full h-full object-contain p-8 transition-all duration-300 group-hover:scale-105"
                @load="mainImgLoaded = true"
                @error="mainImgLoaded = true"
              >
            </div>

            <!-- Mobiele thumbnailstrip -->
            <div v-if="product.images && product.images.length > 1" class="flex gap-2 mt-3 lg:hidden">
              <button
                v-for="(img, i) in product.images.slice(0, 5)"
                :key="i"
                @click="currentImage = img.src"
                :class="['w-[60px] h-[60px] rounded-lg overflow-hidden bg-white flex items-center justify-center p-1 transition-all border-2 shrink-0',
                  currentImage === img.src ? 'border-[#FFD500]' : 'border-transparent hover:border-[#e0e0e0]']"
              >
                <img :src="sized(img.src, 120)" :alt="`${product.name} ${i+1}`" loading="lazy" width="60" height="60" class="w-full h-full object-contain">
              </button>
            </div>
          </div>
        </div>

        <!-- ── Right: Product info (sticky op desktop) ── -->
        <div class="lg:sticky lg:top-[124px] lg:self-start">
          <!-- Product labels (speciale WC-tags) -->
          <div v-if="productLabels.length" class="flex items-center gap-2 flex-wrap mb-3">
            <span
              v-for="label in productLabels"
              :key="label.slug"
              :style="`background:${label.bg};color:${label.color}`"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.8rem] font-bold shadow-sm"
            >
              <i :class="['bi text-[0.75rem]', label.icon]"></i>
              {{ label.text }}
            </span>
          </div>

          <!-- Category chip -->
          <div v-if="productCategory && productCategorySlug" class="mb-2">
            <router-link :to="`/categorie/${productCategorySlug}`" class="inline-flex items-center gap-1.5 text-[0.75rem] font-semibold text-[#888] no-underline hover:text-[#FFD500] transition-colors">
              <i class="bi bi-tag text-[#FFD500] text-[0.7rem]"></i>{{ productCategory }}
            </router-link>
          </div>

          <!-- H1: large, heavy, tight letter-spacing -->
          <h1 class="text-[clamp(1.5rem,3vw,2rem)] font-black text-[#111] leading-tight tracking-[-0.02em] mb-3">{{ product.name }}</h1>

          <!-- Review stars row -->
          <div class="flex items-center gap-2 flex-wrap mb-4">
            <!-- Met reviews: sterren + score -->
            <template v-if="product.rating_count > 0">
              <div class="flex gap-0.5">
                <i v-for="s in 5" :key="s" :class="['bi text-[0.875rem]', s <= displayRating ? 'bi-star-fill text-[#FFD500]' : 'bi-star text-[#ddd]']"></i>
              </div>
              <span class="text-[0.8rem] font-bold text-[#111]">{{ displayRating }}/5</span>
              <span class="text-[0.78rem] text-[#999]">({{ product.rating_count }} reviews)</span>
            </template>
            <!-- Zonder reviews: kwaliteitsbadge i.p.v. lege sterren -->
            <span v-else class="inline-flex items-center gap-1.5 text-[0.78rem] font-semibold text-[#1f7a4d] bg-[#e9f7ef] px-2.5 py-1 rounded-full">
              <i class="bi bi-patch-check-fill"></i> Gecertificeerde kwaliteit
            </span>
            <span class="text-[#e5e7eb] mx-1">·</span>
            <button
              @click="quoteOpen = true"
              class="flex items-center gap-1.5 text-[0.8rem] font-semibold text-[#555] hover:text-[#111] transition-colors bg-transparent border-none p-0 cursor-pointer"
            >
              <i class="bi bi-file-earmark-text text-[#FFD500]"></i>
              Offerte aanvragen
            </button>
          </div>

          <!-- Stock + levertijd -->
          <div class="mb-5">
            <div v-if="product.stock_status === 'instock' && !lowStock" class="flex items-center gap-2 text-[0.84rem]">
              <span class="flex items-center gap-1.5 text-green-600 font-semibold">
                <i class="bi bi-check-circle-fill text-[0.9rem]"></i>
                Op voorraad
              </span>
              <span class="text-[#ccc]">·</span>
              <span v-if="deliveryTime" class="font-medium flex items-center gap-1.5" :style="`color:${deliveryTime.color}`">
                <i :class="['bi', deliveryTime.icon]"></i>
                {{ deliveryTime.label }}<template v-if="deliveryTime.sub"> · {{ deliveryTime.sub }}</template>
              </span>
              <span v-else class="text-[#666]">Bestel voor <strong class="text-[#111]">15:00</strong>, morgen in huis</span>
            </div>
            <div v-else-if="lowStock" class="flex items-center gap-2 text-[0.84rem]">
              <span class="flex items-center gap-1.5 text-orange-600 font-semibold">
                <i class="bi bi-fire text-[0.9rem]"></i>
                Nog {{ stockQuantity }} op voorraad
              </span>
              <span class="text-[#ccc]">·</span>
              <span class="text-[#666]">Bestel snel</span>
            </div>
            <div v-else class="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-lg">
              <i class="bi bi-hourglass-split"></i>
              Tijdelijk uitverkocht - meld je aan voor een melding
            </div>
          </div>

          <!-- Magazijnlocaties (ophalen) -->
          <div v-if="warehouseItems.length" class="mb-4">
            <p class="text-[0.75rem] font-semibold text-[#888] mb-2 flex items-center gap-1.5">
              <i class="bi bi-building text-[#FFD500]"></i>
              Ophalen in magazijn
            </p>
            <div class="flex flex-col gap-1.5">
              <a
                v-for="wh in warehouseItems"
                :key="wh.id"
                :href="wh.mapUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-between bg-white border border-[#e9eaec] hover:border-[#FFD500] rounded-xl px-4 py-2.5 no-underline transition-all group"
              >
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-lg bg-[#f5f5f5] group-hover:bg-[#f0f0f0] flex items-center justify-center transition-colors shrink-0">
                    <i class="bi bi-building text-[0.8rem] text-[#888] transition-colors"></i>
                  </div>
                  <div>
                    <p class="text-[0.85rem] font-semibold text-[#111] leading-none mb-0.5">{{ wh.name }}</p>
                    <p class="text-[0.7rem] text-[#aaa] leading-none">{{ wh.hours }}</p>
                  </div>
                </div>
                <div class="shrink-0 text-right">
                  <span v-if="wh.stock > 0" class="flex items-center gap-1 text-[0.78rem] font-semibold text-green-600">
                    <i class="bi bi-check-circle-fill text-[0.7rem]"></i>
                    {{ wh.stock }} beschikbaar
                  </span>
                  <span v-else class="flex items-center gap-1 text-[0.75rem] text-[#ccc]">
                    <i class="bi bi-x-circle text-[0.7rem]"></i>
                    Niet op voorraad
                  </span>
                  <span class="text-[0.65rem] text-[#aaa] flex items-center gap-1 justify-end mt-0.5">
                    <i class="bi bi-map text-[0.6rem]"></i> Routebeschrijving
                  </span>
                </div>
              </a>
            </div>
          </div>

          <!-- Price block (verborgen voor grouped/bundle - prijs staat in de set-UI) -->
          <div v-if="!isGrouped && !isBundle" class="mb-5">
            <!-- Sale: struck-through old price in grey + savings badge -->
            <div v-if="product.on_sale && savingsAmount > 0" class="flex items-center gap-3 mb-1">
              <span class="text-[1.0625rem] text-[#bbb] line-through font-medium">{{ formatPrice(product.regular_price) }}</span>
              <span class="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-600 text-[0.72rem] font-bold px-2.5 py-0.5 rounded-full">
                <i class="bi bi-tag text-[0.65rem]"></i>
                Bespaar {{ formatPrice(savingsAmount) }} ({{ savingsPercentage }}%)
              </span>
            </div>
            <!-- Main price line: 1.875rem, font-weight 900, #111 -->
            <div class="flex items-baseline gap-2 mb-1">
              <span class="text-[1.875rem] font-black text-[#111] leading-none">{{ formatPrice(currentPrice) }}</span>
              <span class="text-[0.8rem] font-medium text-[#888]">incl. BTW</span>
            </div>
            <!-- Excl. BTW breakdown -->
            <div class="flex items-center gap-1.5 text-[0.75rem] text-[#aaa] flex-wrap">
              <span class="font-medium text-[#666]">{{ formatPrice(priceExclBtw) }}</span>
              <span>excl. BTW</span>
              <span class="text-[#ddd]">·</span>
              <span>BTW 21%: <span class="text-[#555] font-medium">{{ formatPrice(vatAmount) }}</span></span>
            </div>
          </div>

          <!-- Short description in #666 -->
          <div v-if="product.short_description" class="text-[0.875rem] text-[#666] leading-[1.7] mb-5 [&>ul]:list-none [&>ul]:p-0 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-2 [&>ul>li]:mb-1.5 [&>ul>li]:before:content-['✓'] [&>ul>li]:before:text-[#FFD500] [&>ul>li]:before:font-bold [&>ul>li]:before:shrink-0" v-html="product.short_description"></div>

          <!-- ── Add to cart - BUNDLE (simpel product + _is_bundle meta) ── -->
          <div v-if="isBundle" ref="cartAreaRef" class="bundle-section mb-5">
            <p class="text-[0.75rem] font-semibold text-[#888] mb-2.5 flex items-center gap-1.5">
              <i class="bi bi-box-seam text-[#FFD500]"></i>
              Inbegrepen in deze set
            </p>

            <div class="border border-[#e9eaec] rounded-2xl overflow-hidden mb-4 bg-white">
              <div v-if="!bundleItems.length" class="flex items-center justify-center gap-3 py-8 text-[#aaa] text-[0.85rem]">
                <div class="w-4 h-4 border-2 border-[#e0e0e0] border-t-[#FFD500] rounded-full animate-spin"></div>
                Set-inhoud laden...
              </div>
              <div
                v-for="(item, idx) in bundleItems"
                :key="item.id"
                :class="['flex items-center gap-3 px-4 py-3', idx < bundleItems.length - 1 ? 'border-b border-[#f5f5f5]' : '']"
              >
                <div class="relative shrink-0">
                  <img :src="item.images?.[0]?.src || ''" :alt="item.name" class="w-12 h-12 object-contain rounded-xl border border-[#e9eaec] bg-white p-1" />
                  <span class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <i class="bi bi-check text-white text-[0.55rem] font-black"></i>
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[0.85rem] font-semibold text-[#111] leading-snug line-clamp-2">{{ item.name }}</p>
                  <div class="flex items-baseline gap-1.5 mt-0.5">
                    <span v-if="item.on_sale" class="text-[0.7rem] text-[#ccc] line-through">{{ formatPrice(item.regular_price) }}</span>
                    <span class="text-[0.82rem] font-bold text-[#555]">{{ formatPrice(item.regular_price || item.price) }}</span>
                  </div>
                </div>
                <span class="shrink-0 text-[0.72rem] font-bold text-[#aaa] bg-[#f4f4f4] px-2 py-0.5 rounded-full">{{ item.qty || 1 }}×</span>
              </div>
            </div>

            <!-- Korting-banner -->
            <div v-if="bundleSavings > 0" class="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 mb-4 text-[0.82rem]">
              <i class="bi bi-tag-fill text-green-500 shrink-0"></i>
              <span class="text-green-700">Je bespaart <strong>{{ formatPrice(bundleSavings) }}</strong> ({{ bundleSavingsPct }}%) t.o.v. losse aankoop</span>
            </div>

            <!-- Prijs + knop -->
            <div class="flex items-center gap-3">
              <div class="flex flex-col leading-tight">
                <div class="flex items-baseline gap-1.5">
                  <span v-if="bundleSavings > 0" class="text-[0.75rem] text-[#bbb] line-through">{{ formatPrice(bundleRegular) }}</span>
                  <span class="text-[1.35rem] font-extrabold text-[#111]">{{ formatPrice(bundleSetPrice) }}</span>
                </div>
                <span class="text-[0.7rem] text-[#aaa]">voor de complete set · incl. BTW</span>
              </div>
              <button
                @click="addBundleToCart"
                :disabled="adding || !bundleItems.length"
                class="flex-1 flex items-center justify-center gap-2.5 font-bold text-[0.9375rem] py-3 rounded-xl transition-all disabled:opacity-60 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] hover:-translate-y-[1px] hover:shadow-lg hover:shadow-[#FFD500]/30"
              >
                <i :class="['bi', adding ? 'bi-arrow-repeat spin' : 'bi-cart']"></i>
                <span>{{ adding ? 'Toevoegen...' : 'Set in winkelwagen' }}</span>
              </button>
              <button
                @click="toggleWishlist"
                :class="['w-12 h-12 flex items-center justify-center rounded-xl border transition-all text-[1.1rem] shrink-0',
                  inWishlist ? 'bg-[#fffbeb] border-[#FFD500] text-[#FFD500]' : 'bg-white border-[#e5e7eb] text-[#bbb] hover:border-[#FFD500] hover:text-[#FFD500]']"
              ><i :class="['bi', inWishlist ? 'bi-heart-fill' : 'bi-heart']"></i></button>
            </div>
          </div>

          <!-- ── Add to cart - GROUPED (bundle/set) ── -->
          <div v-if="isGrouped" ref="cartAreaRef" class="grouped-section mb-5">

            <!-- Set-label -->
            <p class="text-[0.75rem] font-semibold text-[#888] mb-2.5 flex items-center gap-1.5">
              <i class="bi bi-box-seam text-[#FFD500]"></i>
              Inbegrepen in deze set
            </p>

            <!-- Sub-producten lijst -->
            <div class="border border-[#e9eaec] rounded-2xl overflow-hidden mb-4 bg-white">
              <!-- Loading -->
              <div v-if="!groupedItems.length" class="flex items-center justify-center gap-3 py-8 text-[#aaa] text-[0.85rem]">
                <div class="w-4 h-4 border-2 border-[#e0e0e0] border-t-[#FFD500] rounded-full animate-spin"></div>
                Set-inhoud laden...
              </div>

              <div
                v-for="(item, idx) in groupedItems"
                :key="item.id"
                :class="['flex items-center gap-3 px-4 py-3', idx < groupedItems.length - 1 ? 'border-b border-[#f5f5f5]' : '']"
              >
                <!-- Check-badge + afbeelding -->
                <div class="relative shrink-0">
                  <img
                    :src="item.images?.[0]?.src || ''"
                    :alt="item.name"
                    class="w-12 h-12 object-contain rounded-xl border border-[#e9eaec] bg-white p-1"
                  />
                  <span class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <i class="bi bi-check text-white text-[0.55rem] font-black"></i>
                  </span>
                </div>

                <!-- Naam + prijs -->
                <div class="flex-1 min-w-0">
                  <p class="text-[0.85rem] font-semibold text-[#111] leading-snug line-clamp-2">{{ item.name }}</p>
                  <div class="flex items-baseline gap-1.5 mt-0.5">
                    <span v-if="item.on_sale" class="text-[0.7rem] text-[#ccc] line-through">{{ formatPrice(item.regular_price) }}</span>
                    <span class="text-[0.85rem] font-bold" :class="item.on_sale ? 'text-green-600' : 'text-[#555]'">{{ formatPrice(item.price) }}</span>
                  </div>
                </div>

                <!-- 1× badge -->
                <span class="shrink-0 text-[0.72rem] font-bold text-[#aaa] bg-[#f4f4f4] px-2 py-0.5 rounded-full">1×</span>
              </div>
            </div>

            <!-- Samenvatting: korting-banner -->
            <div v-if="groupedSavings > 0" class="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 mb-4 text-[0.82rem]">
              <i class="bi bi-tag-fill text-green-500 shrink-0"></i>
              <span class="text-green-700">
                Je bespaart <strong>{{ formatPrice(groupedSavings) }}</strong> ({{ groupedSavingsPct }}%) t.o.v. losse aankoop
              </span>
            </div>

            <!-- Prijs + knop rij -->
            <div class="flex items-center gap-3">
              <div class="flex flex-col leading-tight">
                <div class="flex items-baseline gap-1.5">
                  <span v-if="groupedSavings > 0" class="text-[0.75rem] text-[#bbb] line-through">{{ formatPrice(groupedRegular) }}</span>
                  <span class="text-[1.35rem] font-extrabold text-[#111]">{{ formatPrice(groupedTotal) }}</span>
                </div>
                <span class="text-[0.7rem] text-[#aaa]">voor de complete set</span>
              </div>

              <button
                @click="addGroupedToCart"
                :disabled="adding"
                class="flex-1 flex items-center justify-center gap-2.5 font-bold text-[0.9375rem] py-3 rounded-xl transition-all disabled:opacity-60 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] hover:-translate-y-[1px] hover:shadow-lg hover:shadow-[#FFD500]/30"
              >
                <i :class="['bi', adding ? 'bi-arrow-repeat spin' : 'bi-cart']"></i>
                <span>{{ adding ? 'Toevoegen...' : 'Set in winkelwagen' }}</span>
              </button>

              <!-- Wishlist -->
              <button
                @click="toggleWishlist"
                :class="['w-12 h-12 flex items-center justify-center rounded-xl border transition-all text-[1.1rem] shrink-0',
                  inWishlist ? 'bg-[#fffbeb] border-[#FFD500] text-[#FFD500]' : 'bg-white border-[#e5e7eb] text-[#bbb] hover:border-[#FFD500] hover:text-[#FFD500]']"
              ><i :class="['bi', inWishlist ? 'bi-heart-fill' : 'bi-heart']"></i></button>
            </div>
          </div>

          <!-- ── Add to cart - NORMAAL ── -->
          <div v-else-if="!isBundle" ref="cartAreaRef" class="mb-5">

            <!-- Uitverkocht: toon voorraad-melding formulier -->
            <div v-if="product.stock_status === 'outofstock' && !product.backorders_allowed">
              <StockNotify
                :productId="product.id"
                :productName="product.name"
                :productSlug="product.slug"
                :productImage="currentImage"
              />
            </div>

            <!-- Op voorraad: normale add-to-cart -->
            <div v-else-if="product.stock_status === 'instock' || product.backorders_allowed">
              <!-- Quantity + Add-to-cart row -->
              <div class="flex gap-3 mb-3">
                <!-- Quantity selector with +/- buttons -->
                <div class="flex items-center border border-[#e5e7eb] rounded-[8px] overflow-hidden shrink-0 bg-white">
                  <button @click="decreaseQuantity" :disabled="quantity <= 1" class="w-10 h-[52px] flex items-center justify-center text-[#666] hover:bg-white disabled:opacity-40 transition-colors border-r border-[#e5e7eb]">
                    <i class="bi bi-dash"></i>
                  </button>
                  <input
                    v-model.number="quantity"
                    type="number"
                    min="1"
                    :max="maxQuantity"
                    class="w-12 h-[52px] text-center font-semibold text-[#111] border-none outline-none bg-white text-[0.9rem]"
                  >
                  <button @click="increaseQuantity" :disabled="quantity >= maxQuantity" class="w-10 h-[52px] flex items-center justify-center text-[#666] hover:bg-white disabled:opacity-40 transition-colors border-l border-[#e5e7eb]">
                    <i class="bi bi-plus"></i>
                  </button>
                </div>

                <!-- "In winkelwagen" button: full-width, yellow, cart-plus icon -->
                <button
                  @click="addToCart"
                  :disabled="adding"
                  class="flex-1 flex items-center justify-center gap-2 font-extrabold text-[1rem] py-[1rem] rounded-[8px] transition-all bg-[#FFD500] hover:bg-[#ffb800] text-[#111] hover:-translate-y-[1px] hover:shadow-lg hover:shadow-[#FFD500]/30 disabled:opacity-60"
                >
                  <i :class="['bi text-[1.1rem]', adding ? 'bi-arrow-repeat spin' : 'bi-cart-plus']"></i>
                  <span>{{ addToCartText }}</span>
                </button>
              </div>

              <!-- Wishlist: secondary outlined button -->
              <button
                @click="toggleWishlist"
                :class="['w-full flex items-center justify-center gap-2 h-11 rounded-[8px] border transition-all text-[0.875rem] font-semibold',
                  inWishlist
                    ? 'bg-[#fffbeb] border-[#FFD500] text-[#7a5f00]'
                    : 'bg-white border-[#e5e7eb] text-[#555] hover:border-[#FFD500] hover:text-[#7a5f00]']"
              >
                <i :class="['bi text-[0.9rem]', inWishlist ? 'bi-heart-fill text-[#FFD500]' : 'bi-heart']"></i>
                {{ inWishlist ? 'Opgeslagen in verlanglijst' : 'Toevoegen aan verlanglijst' }}
              </button>

              <!-- Staffelprijs / volumekorting: opent de offerte-flow -->
              <button
                @click="quoteOpen = true"
                class="w-full flex items-center justify-center gap-2 mt-2 py-2.5 rounded-[8px] bg-[#f6f8fa] hover:bg-[#eef1f4] border border-[#e5e7eb] text-[#333] text-[0.82rem] font-semibold transition-colors"
              >
                <i class="bi bi-boxes text-[#b8860b]"></i>
                Meerdere stuks of zakelijk? Vraag een staffelprijs aan
              </button>

            </div>
            </div><!-- /v-else op voorraad -->
          </div>

          <!-- Added to cart feedback -->
          <transition name="slide-down">
            <div v-if="addedFeedback" class="flex items-center gap-2.5 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-[0.85rem] font-medium mb-4">
              <i class="bi bi-check-circle text-[1.1rem]"></i>
              Product toegevoegd aan je winkelwagen!
              <router-link to="/cart" class="ml-auto text-green-700 font-bold hover:underline no-underline">Bekijk →</router-link>
            </div>
          </transition>

          <!-- Trust strip: 2×2 beloftes -->
          <div class="border-t border-[#f0f0f0] pt-4 mt-1 mb-5">
            <div class="grid grid-cols-2 gap-x-5 gap-y-3">
              <div class="flex items-center gap-2 text-[0.8rem] text-[#444]">
                <i class="bi bi-clock text-[#FFD500] text-[1rem] shrink-0"></i>
                <span>Voor 15:00 → morgen in huis</span>
              </div>
              <div class="flex items-center gap-2 text-[0.8rem] text-[#444]">
                <i class="bi bi-shield-check text-[#FFD500] text-[1rem] shrink-0"></i>
                <span>2 jaar garantie</span>
              </div>
              <div class="flex items-center gap-2 text-[0.8rem] text-[#444]">
                <i class="bi bi-truck text-[#FFD500] text-[1rem] shrink-0"></i>
                <span>Gratis verzending v.a. €50</span>
              </div>
              <div class="flex items-center gap-2 text-[0.8rem] text-[#444]">
                <i class="bi bi-arrow-clockwise text-[#FFD500] text-[1rem] shrink-0"></i>
                <span>30 dagen retour</span>
              </div>
            </div>
          </div>

          <!-- Achteraf betalen (conversie-argument) -->
          <div class="flex items-center gap-2 text-[0.82rem] text-[#333] bg-[#fafafa] border border-[#eee] rounded-lg px-3 py-2 mb-4">
            <i class="bi bi-credit-card-2-back text-[#FFD500] text-[1rem] shrink-0"></i>
            <span>Betaal met <strong>iDEAL</strong> of kies voor <strong>achteraf betalen met Klarna</strong>.</span>
          </div>

          <!-- Payment methods -->
          <div class="flex items-center gap-1.5 flex-wrap mb-4">
            <span class="text-[0.75rem] text-[#aaa] font-medium shrink-0 mr-0.5">Veilig betalen:</span>
            <span
              v-for="pm in paymentMethods"
              :key="pm.key"
              :title="pm.label"
              class="inline-flex items-center justify-center rounded border border-[#e5e7eb] overflow-hidden shadow-sm shrink-0 bg-white"
              style="width:52px;height:32px;padding:3px;"
            >
              <img
                :src="`/img/payment/${pm.key}.svg`"
                :alt="pm.label"
                class="w-full h-full object-contain"
                loading="lazy"
              />
            </span>
            <span class="flex items-center gap-1 text-[0.7rem] text-[#aaa] ml-1 shrink-0">
              <i class="bi bi-shield-lock-fill text-green-500 text-[0.75rem]"></i> SSL
            </span>
          </div>

          <!-- Hulp inline -->
          <p class="text-[0.78rem] text-[#999] mt-1">
            Vraag over dit product?
            <a href="/klantenservice#livechat" class="text-[#555] font-semibold no-underline hover:text-[#FFD500] transition-colors">Chat met ons</a>
            <span class="mx-1 text-[#ddd]">·</span>
            <a href="mailto:info@plug2charge.nl" class="text-[#555] font-semibold no-underline hover:text-[#FFD500] transition-colors">Stuur een mail</a>
          </p>

          <!-- Product meta (SKU) -->
          <div v-if="product.sku" class="mt-4 pt-4 border-t border-[#f0f0f0] text-[0.78rem] text-[#888]">
            <span class="font-medium text-[#555]">SKU:</span> {{ product.sku }}
          </div>

          <!-- Gerelateerde categorieën chips -->
          <div v-if="product.categories?.filter(c => !c.slug?.includes('uncategor')).length" class="mt-4 pt-4 border-t border-[#f0f0f0]">
            <p class="text-[0.75rem] font-semibold text-[#888] mb-2.5">Gerelateerde categorieën</p>
            <div class="flex flex-wrap gap-2">
              <router-link
                v-for="cat in product.categories.filter(c => !c.slug?.includes('uncategor') && !c.name?.toLowerCase().includes('niet gecategoriseerd'))"
                :key="cat.id"
                :to="`/categorie/${cat.slug}`"
                class="inline-flex items-center gap-1.5 bg-white border border-[#e9eaec] hover:border-[#FFD500] hover:bg-[#fffbeb] text-[#333] hover:text-[#111] text-[0.78rem] font-semibold px-3 py-1.5 rounded-full no-underline transition-all"
              >
                <i class="bi bi-tag text-[#FFD500] text-[0.65rem]"></i>
                {{ cat.name }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      </div><!-- /.product-content-inner -->
      </div><!-- /.product-white-section -->

      <!-- Grijs gedeelte: tabs, aanbevelingen, garantie -->
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6 pt-6 pb-10">

      <!-- ── Tabs: Description / Specs / Reviews / Delivery ── -->
      <!-- Altijd tonen zodra het product geladen is: ook zonder omschrijving zijn
           Specificaties, Bezorging en Reviews waardevol. De Beschrijving-tab zelf
           verschijnt alleen als er een omschrijving is (zie tabs.show). -->
      <div v-if="product" id="product-tabs" class="bg-white rounded-2xl border border-[#e9eaec] overflow-hidden mb-8">

        <!-- Tab headers -->
        <div class="flex border-b border-[#f0f0f0] overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            v-show="tab.show"
            @click="activeTab = tab.id"
            :class="['flex items-center gap-2 px-6 py-4 text-[0.875rem] font-semibold whitespace-nowrap border-b-2 transition-all',
              activeTab === tab.id
                ? 'border-[#FFD500] text-[#111]'
                : 'border-transparent text-[#888] hover:text-[#333]']"
          >
            <i :class="['bi text-[0.9rem]', tab.icon]"></i>
            {{ tab.label }}
            <span v-if="tab.badge" class="bg-[#f0f0f0] text-[#666] text-[0.7rem] font-bold px-1.5 py-0.5 rounded-full">{{ tab.badge }}</span>
          </button>
        </div>

        <!-- Tab content -->
        <div class="p-6 lg:p-8">

          <!-- Description: prose-product styling + AI-verrijkte blokken -->
          <div v-show="activeTab === 'description'" class="prose-product">
            <div v-if="product.description" v-html="product.description"></div>
            <div v-if="product.applications" class="mt-6" v-html="product.applications"></div>
            <div v-if="product.benefits?.length" class="mt-6">
              <h3>Belangrijkste voordelen</h3>
              <ul>
                <li v-for="(b, i) in product.benefits" :key="'b'+i">{{ b }}</li>
              </ul>
            </div>
            <div v-if="product.product_faq?.length" class="mt-6">
              <h3>Veelgestelde vragen</h3>
              <div v-for="(f, i) in product.product_faq" :key="'f'+i" class="mb-3">
                <p class="font-semibold text-[#111] mb-1">{{ f.q }}</p>
                <p>{{ f.a }}</p>
              </div>
            </div>
          </div>

          <!-- Specs -->
          <div v-show="activeTab === 'specs'">
            <h3 class="text-[1rem] font-bold text-[#111] mb-4">Technische specificaties</h3>
            <table class="w-full text-[0.875rem]">
              <tbody>
                <tr v-if="product.weight" class="border-b border-[#f0f0f0]">
                  <td class="py-3 pr-6 font-semibold text-[#555] w-40">Gewicht</td>
                  <td class="py-3 text-[#333]">{{ product.weight }} kg</td>
                </tr>
                <tr v-if="product.dimensions?.length" class="border-b border-[#f0f0f0]">
                  <td class="py-3 pr-6 font-semibold text-[#555]">Afmetingen</td>
                  <td class="py-3 text-[#333]">{{ product.dimensions.length }} × {{ product.dimensions.width }} × {{ product.dimensions.height }} cm</td>
                </tr>
                <tr v-if="product.sku" class="border-b border-[#f0f0f0]">
                  <td class="py-3 pr-6 font-semibold text-[#555]">SKU</td>
                  <td class="py-3 text-[#333]">{{ product.sku }}</td>
                </tr>
                <tr v-for="attr in product.attributes" :key="attr.id" class="border-b border-[#f0f0f0]">
                  <td class="py-3 pr-6 font-semibold text-[#555]">{{ attr.name }}</td>
                  <td class="py-3 text-[#333]">{{ attr.options?.join(', ') }}</td>
                </tr>
                <tr v-if="!product.weight && !product.sku && !product.attributes?.length">
                  <td colspan="2" class="py-6 text-center text-[#888] text-[0.875rem]">Specificaties worden binnenkort toegevoegd.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Datasheets -->
          <div v-show="activeTab === 'datasheets'">
            <h3 class="text-[1rem] font-bold text-[#111] mb-4">Technische documentatie</h3>
            <div class="flex flex-col gap-3">
              <a
                v-for="(sheet, i) in datasheets"
                :key="i"
                :href="sheet.url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-4 p-4 border border-[#e9eaec] rounded-xl hover:border-[#FFD500] hover:bg-[#fffbeb] transition-all group no-underline"
              >
                <div class="w-11 h-11 bg-[#fff0f0] rounded-lg flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] transition-colors">
                  <i class="bi bi-file-earmark-pdf text-[#e53e3e] text-[1.25rem] group-hover:text-[#000]"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="font-semibold text-[#111] text-[0.9rem] truncate">{{ sheet.name }}</div>
                  <div class="text-[#888] text-[0.75rem] truncate">{{ sheet.url.split('/').pop() }}</div>
                </div>
                <div class="flex items-center gap-1.5 text-[0.8rem] font-semibold text-[#666] group-hover:text-[#111] shrink-0">
                  <i class="bi bi-download"></i>
                  Downloaden
                </div>
              </a>
            </div>
          </div>

          <!-- Reviews -->
          <div v-show="activeTab === 'reviews'">

            <!-- Rating summary (alleen als er reviews zijn) -->
            <div v-if="hasReviews" class="flex items-center gap-8 mb-8 pb-6 border-b border-[#f0f0f0]">
              <div class="text-center shrink-0">
                <div class="text-[3rem] font-extrabold text-[#111] leading-none">{{ formatRating(product.average_rating) }}</div>
                <div class="flex gap-1 justify-center mt-2">
                  <i v-for="s in 5" :key="s" :class="['bi text-[1rem]', s <= Math.round(product.average_rating) ? 'bi-star-fill text-[#FFD500]' : 'bi-star text-[#ddd]']"></i>
                </div>
                <p class="text-[0.75rem] text-[#888] mt-1">{{ product.rating_count }} reviews</p>
              </div>
              <div class="flex-1 flex flex-col gap-2">
                <div v-for="r in [5,4,3,2,1]" :key="r" class="flex items-center gap-3">
                  <span class="text-[0.78rem] text-[#666] w-3">{{ r }}</span>
                  <i class="bi bi-star-fill text-[#FFD500] text-[0.65rem]"></i>
                  <div class="flex-1 h-2 bg-[#f0f0f0] rounded-full overflow-hidden">
                    <div class="h-full bg-[#FFD500] rounded-full" :style="{ width: `${getRatingPercentage(r)}%` }"></div>
                  </div>
                  <span class="text-[0.75rem] text-[#888] w-8 text-right">{{ getRatingPercentage(r) }}%</span>
                </div>
              </div>
            </div>

            <!-- Reviews laden -->
            <div v-if="reviewsLoading" class="flex justify-center py-8">
              <div class="w-8 h-8 rounded-full border-4 border-[#f0f0f0] border-t-[#FFD500] animate-spin"></div>
            </div>

            <!-- Reviewlijst -->
            <div v-else-if="reviews.length" class="flex flex-col divide-y divide-[#f3f4f6]">
              <div v-for="review in reviews" :key="review.id" class="py-5 first:pt-0">
                <div class="flex items-start gap-4">
                  <!-- Avatar -->
                  <div class="w-10 h-10 rounded-full bg-[#FFD500] flex items-center justify-center text-[#111] font-bold text-[0.875rem] shrink-0">
                    {{ review.reviewer.charAt(0).toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap mb-1">
                      <span class="font-semibold text-[0.875rem] text-[#111]">{{ review.reviewer }}</span>
                      <span v-if="review.verified" class="inline-flex items-center gap-1 text-[0.68rem] text-green-600 font-semibold">
                        <i class="bi bi-patch-check-fill"></i> Geverifieerde aankoop
                      </span>
                      <span class="text-[0.72rem] text-[#aaa] ml-auto">{{ formatReviewDate(review.date_created) }}</span>
                    </div>
                    <!-- Sterren -->
                    <div class="flex gap-0.5 mb-2">
                      <i v-for="s in 5" :key="s" :class="['bi text-[0.8rem]', s <= review.rating ? 'bi-star-fill text-[#FFD500]' : 'bi-star text-[#ddd]']"></i>
                    </div>
                    <!-- Tekst -->
                    <p class="text-[0.875rem] text-[#444] leading-relaxed" v-html="review.review"></p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Lege staat -->
            <div v-else class="text-center py-10">
              <div class="w-16 h-16 rounded-2xl bg-[#f8f9fa] flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-chat-square-dots text-[2rem] text-[#ddd]"></i>
              </div>
              <p class="font-bold text-[#111] mb-1">Er zijn nog geen reviews voor dit product</p>
              <p class="text-[0.875rem] text-[#888] mb-5">Wees de eerste en help andere kopers!</p>
              <a
                :href="`https://plug2charge.nl/product/${product.slug}#respond`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]"
              >
                <i class="bi bi-pencil"></i>
                Schrijf een review
              </a>
            </div>
          </div>

          <!-- Delivery -->
          <div v-show="activeTab === 'delivery'" class="grid sm:grid-cols-2 gap-8">
            <div>
              <h3 class="flex items-center gap-2 text-[1rem] font-bold text-[#111] mb-4">
                <i class="bi bi-truck text-[#FFD500]"></i> Bezorging
              </h3>
              <ul class="flex flex-col gap-2.5 text-[0.875rem]">
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span><strong>Gratis verzending</strong> vanaf €50</span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span>Bestel voor <strong>15:00 uur</strong> = morgen in huis</span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span v-if="deliveryTime">Levertijd: <strong>{{ deliveryTime.sub || deliveryTime.label }}</strong></span><span v-else>Levertijd: <strong>1-2 werkdagen</strong></span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span><strong>Track & Trace</strong> bij elke bestelling</span></li>
              </ul>
            </div>
            <div>
              <h3 class="flex items-center gap-2 text-[1rem] font-bold text-[#111] mb-4">
                <i class="bi bi-arrow-clockwise text-[#FFD500]"></i> Retourneren
              </h3>
              <ul class="flex flex-col gap-2.5 text-[0.875rem]">
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span><strong>30 dagen</strong> bedenktijd</span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span><strong>Gratis</strong> retourneren</span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span>Geld terug binnen <strong>5 werkdagen</strong></span></li>
                <li class="flex items-start gap-2"><i class="bi bi-check2 text-green-500 mt-0.5 shrink-0"></i><span>Gewoon terugsturen, wij regelen de rest</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Upsells: Misschien vind je dit ook interessant ── -->
      <div v-if="upsells.length" class="mb-10">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-[1.125rem] font-bold text-[#111] m-0">Misschien vind je dit ook interessant</h2>
          <router-link
            v-if="productCategorySlug"
            :to="`/categorie/${productCategorySlug}`"
            class="text-[0.8rem] font-semibold text-[#FFD500] hover:text-[#ffb800] no-underline transition-colors flex items-center gap-1"
          >
            Meer bekijken <i class="bi bi-arrow-right text-[0.75rem]"></i>
          </router-link>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <ProductCard
            v-for="(p, i) in upsells"
            :key="p.id"
            :product="p"
            :index="i"
            list-name="Upsells"
          />
        </div>
      </div>

      <!-- ── Vaak samen gekocht ── -->
      <div v-if="crossSells.length" class="mb-10">
        <h2 class="text-[1.125rem] font-bold text-[#111] mb-4">Vaak samen gekocht</h2>
        <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          <router-link
            v-for="p in crossSells"
            :key="p.id"
            :to="`/product/${p.slug}`"
            class="group bg-white border border-[#e9eaec] hover:border-[#FFD500] rounded-xl p-3 flex items-center gap-3 no-underline transition-all"
          >
            <!-- Afbeelding -->
            <div class="w-[68px] h-[68px] rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden">
              <img
                v-if="p.thumbnail"
                :src="p.thumbnail"
                :alt="p.name"
                class="w-full h-full object-contain p-1"
                loading="lazy"
              >
              <i v-else class="bi bi-box-seam text-[1.5rem] text-[#ccc]"></i>
            </div>
            <!-- Info -->
            <div class="flex-1 min-w-0">
              <p class="text-[0.82rem] font-semibold text-[#111] leading-snug line-clamp-2 group-hover:text-[#7a5f00] transition-colors">{{ p.name }}</p>
              <div class="flex items-baseline gap-1.5 mt-1">
                <span v-if="p.on_sale" class="text-[0.7rem] text-[#bbb] line-through">{{ formatPrice(p.regular_price) }}</span>
                <span class="text-[0.9rem] font-extrabold text-[#111]">{{ formatPrice(p.price) }}</span>
              </div>
              <span class="text-[0.68rem] text-[#aaa]">incl. btw</span>
            </div>
            <!-- Cart knop -->
            <button
              @click.prevent="quickAddToCart(p)"
              class="w-9 h-9 rounded-xl bg-[#FFD500] hover:bg-[#ffb800] flex items-center justify-center shrink-0 transition-colors"
              title="Toevoegen"
            >
              <i class="bi bi-cart text-[0.875rem] text-[#111]"></i>
            </button>
          </router-link>
        </div>
      </div>

      <!-- Garantie banner -->
      <div class="bg-white rounded-xl border border-[#e9eaec] px-5 py-4 mb-6">
        <div class="flex items-start gap-4 mb-3">
          <div class="w-10 h-10 rounded-xl bg-[#f8f9fa] border border-[#e9eaec] flex items-center justify-center shrink-0">
            <i class="bi bi-shield-check text-[1.2rem] text-[#FFD500]"></i>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-[#111] font-bold text-[0.9375rem] mb-1">Tevredenheidsgarantie</h3>
            <p class="text-[#666] text-[0.83rem] leading-relaxed m-0">Bevalt het toch niet? Stuur het terug binnen 30 dagen. We zetten het bedrag direct terug op je rekening.</p>
          </div>
        </div>
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div class="flex flex-wrap gap-x-4 gap-y-1 text-[0.76rem] text-[#888]">
            <span class="flex items-center gap-1"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> Gratis retour</span>
            <span class="flex items-center gap-1"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> Volledige terugbetaling</span>
            <span class="flex items-center gap-1"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> Geen gedoe</span>
          </div>
          <router-link to="/retourneren" class="shrink-0 border border-[#e9eaec] bg-[#f8f9fa] hover:border-[#FFD500] text-[#111] font-semibold px-4 py-1.5 rounded-lg no-underline text-[0.85rem] transition-all">
            Meer info
          </router-link>
        </div>
      </div>

      </div><!-- /.grey-section -->

    <!-- Persoonlijk advies CTA (buiten de max-w container) -->
    <HomeCtaBanner v-if="product" class="mt-2" />

    <!-- ── Foto lightbox ─────────────────────────────────────────────────── -->
    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="lightboxOpen"
          class="fixed inset-0 z-[1200] bg-black/90 flex items-center justify-center p-4"
          @click.self="lightboxOpen = false"
        >
          <!-- Sluit knop -->
          <button
            @click="lightboxOpen = false"
            class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <i class="bi bi-x-lg text-[1.1rem]"></i>
          </button>
          <!-- Afbeelding (hoge resolutie voor de zoom-weergave) -->
          <img
            :src="sized(currentImage, 1400)"
            :alt="product?.name"
            class="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            style="max-height: 90vh; max-width: 90vw;"
          >
          <!-- Thumbnail navigatie -->
          <div v-if="product?.images?.length > 1" class="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            <button
              v-for="(img, i) in product.images"
              :key="i"
              @click="currentImage = img.src"
              :class="['w-12 h-12 rounded-lg border-2 overflow-hidden bg-white/10 transition-all flex items-center justify-center p-1',
                currentImage === img.src ? 'border-[#FFD500]' : 'border-white/20 hover:border-white/50']"
            >
              <img :src="sized(img.src, 100)" :alt="`foto ${i+1}`" loading="lazy" class="max-w-full max-h-full object-contain">
            </button>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- ── Offerte aanvraag modal ──────────────────────────────────────────── -->
    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="quoteOpen"
          class="fixed inset-0 z-[1100] bg-black/60 flex items-end sm:items-center justify-center p-4 sm:p-6"
          @click.self="quoteOpen = false"
        >
          <div ref="quoteModalEl" class="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               role="dialog" aria-modal="true" aria-label="Offerte aanvragen">

            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-5 border-b border-[#f0f0f0]">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-[#FFD500] rounded-xl flex items-center justify-center shrink-0">
                  <i class="bi bi-file-earmark-text text-[#111] text-[1.1rem]"></i>
                </div>
                <div>
                  <h2 class="text-[1rem] font-extrabold text-[#111] m-0">Offerte aanvragen</h2>
                  <p class="text-[0.78rem] text-[#888] m-0">Wij reageren binnen 1 werkdag</p>
                </div>
              </div>
              <button @click="quoteOpen = false" aria-label="Sluiten" class="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[#f4f4f4] transition-colors text-[#888]">
                <i class="bi bi-x-lg text-[1rem]" aria-hidden="true"></i>
              </button>
            </div>

            <!-- Form or success -->
            <div class="px-6 py-5">

              <!-- Success state -->
              <div v-if="quoteDone" class="flex flex-col items-center text-center py-4 gap-4">
                <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <i class="bi bi-check2-circle text-green-500 text-[2rem]"></i>
                </div>
                <div>
                  <h3 class="text-[1rem] font-bold text-[#111] mb-1">Aanvraag ontvangen!</h3>
                  <p class="text-[0.85rem] text-[#666]">We nemen zo snel mogelijk contact op via <strong>{{ quoteSentEmail }}</strong>.</p>
                </div>
                <button @click="quoteOpen = false; quoteDone = false" class="bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-2.5 rounded-xl transition-all text-[0.875rem]">
                  Sluiten
                </button>
              </div>

              <!-- Form -->
              <form v-else @submit.prevent="submitQuote" class="flex flex-col gap-4">
                <input v-model="quoteForm.website" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />

                <!-- Product naam (readonly) -->
                <div class="bg-[#f8f9fa] rounded-xl px-4 py-3 flex items-center gap-3">
                  <img v-if="currentImage" :src="sized(currentImage, 88)" :alt="product.name" class="w-10 h-10 object-contain rounded-lg border border-[#e9eaec] bg-white p-1 shrink-0">
                  <div class="min-w-0">
                    <p class="text-[0.72rem] text-[#aaa] font-medium mb-0.5">Product</p>
                    <p class="text-[0.88rem] font-semibold text-[#111] truncate">{{ product.name }}</p>
                  </div>
                </div>

                <!-- Naam + Bedrijf -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">Naam <span class="text-red-500">*</span></label>
                    <input v-model="quoteForm.name" type="text" required placeholder="Jouw naam"
                      class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
                  </div>
                  <div>
                    <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">Bedrijf</label>
                    <input v-model="quoteForm.company" type="text" placeholder="Bedrijfsnaam (optioneel)"
                      class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
                  </div>
                </div>

                <!-- E-mail + Telefoon -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">E-mailadres <span class="text-red-500">*</span></label>
                    <input v-model="quoteForm.email" type="email" required placeholder="jouw@email.nl"
                      class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
                  </div>
                  <div>
                    <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">Telefoonnummer</label>
                    <input v-model="quoteForm.phone" type="tel" placeholder="+31 6 12345678"
                      class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
                  </div>
                </div>

                <!-- Aantal -->
                <div>
                  <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">Gewenst aantal</label>
                  <input v-model.number="quoteForm.quantity" type="number" min="1" placeholder="bv. 10"
                    class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
                </div>

                <!-- Bericht -->
                <div>
                  <label class="block text-[0.8rem] font-semibold text-[#333] mb-1.5">Aanvullende informatie <span class="text-red-500">*</span></label>
                  <textarea v-model="quoteForm.message" required rows="3" placeholder="Beschrijf je wensen, installatie-situatie of andere vragen..."
                    class="w-full px-3.5 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all resize-none"></textarea>
                </div>

                <p v-if="quoteError" class="text-[0.8rem] text-red-500 flex items-center gap-1.5">
                  <i class="bi bi-exclamation-circle"></i> {{ quoteError }}
                </p>

                <div class="flex gap-3 pt-1">
                  <button type="button" @click="quoteOpen = false"
                    class="flex-1 py-3 rounded-xl border border-[#e5e7eb] text-[#555] font-semibold text-[0.875rem] hover:bg-white transition-all">
                    Annuleren
                  </button>
                  <button type="submit" :disabled="quoteLoading"
                    class="flex-1 py-3 rounded-xl bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold text-[0.875rem] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                    <i :class="['bi', quoteLoading ? 'bi-arrow-repeat spin' : 'bi-send']"></i>
                    {{ quoteLoading ? 'Versturen...' : 'Aanvraag versturen' }}
                  </button>
                </div>

              </form>
            </div>

          </div>
        </div>
      </transition>
    </teleport>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useEscToClose } from '@composables/useEscToClose'
import { useFocusTrap } from '@composables/useFocusTrap'
import { useRoute } from 'vue-router'
import HomeCtaBanner from '@components/HomeCtaBanner.vue'
import ProductCard from '@components/ProductCard.vue'
import StockNotify from '@components/StockNotify.vue'
import wooApi from '@/api/woocommerce.js'
import { useProductStore } from '@stores/products'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import { useAnalytics } from '@composables/useAnalytics'
import { useCartModal } from '@composables/useCartModal'

const route         = useRoute()
const productStore  = useProductStore()
const cartStore     = useCartStore()
const wishlistStore = useWishlistStore()
const { open: openCartModal } = useCartModal()
const { trackViewItem, trackAddToCart, trackWishlist, trackQuoteRequest } = useAnalytics()

// ── State ───────────────────────────────────────────────────────────────────
const quantity     = ref(1)
const adding       = ref(false)
const addedFeedback = ref(false)
const groupedItems  = ref([])   // sub-producten van een grouped product
const inWishlist   = computed(() => product.value ? wishlistStore.isInWishlist(product.value.id) : false)
// Initialiseer vanuit store-data zodat SSR en client dezelfde afbeelding renderen.
// ref('') zou leiden tot een hydration mismatch: SSR rendert placeholder maar
// client wil de echte afbeeldings-URL → Vue vervangt de component → transition
// start op opacity:0 → pagina blijft onzichtbaar.
const currentImage = ref(productStore.currentProduct?.images?.[0]?.src || '')
const activeTab    = ref('description')

// Sized WebP via de media-proxy (verkleint + WebP). currentImage zelf blijft ruw
// (voor de === vergelijkingen en de lightbox in hoge resolutie).
const sized = (url, w) => {
  if (!url) return url
  const i = url.indexOf('/wp-content/')
  return i === -1 ? url : `${url.slice(i)}?w=${w}`
}

// LQIP blur-up voor de grote productfoto. Reset bij het wisselen van foto
// (thumbnails), zodat elke nieuwe afbeelding ook een blur-up krijgt.
const mainImgLoaded = ref(false)
const mainImgEl     = ref(null)
const lqipSrc = computed(() => currentImage.value ? sized(currentImage.value, 32) : '')
watch(currentImage, () => {
  mainImgLoaded.value = false
  nextTick(() => { if (mainImgEl.value?.complete) mainImgLoaded.value = true })
})
const viewCount    = ref(0) // SSR-safe: 0 op server, random waarde na hydration in onMounted
const dealTimeLeft = ref('')  // leeg op SSR - wordt in onMounted ingesteld om hydration mismatch te voorkomen
let dealInterval   = null

// Sticky bar
const cartAreaRef   = ref(null)
const showStickyBar = ref(false)
let stickyObserver  = null

// Reviews
const reviews        = ref([])
const reviewsLoading = ref(false)

// Cross-sells
const crossSells = ref([])

// Upsells
const upsells = ref([])

// Lightbox
const lightboxOpen = ref(false)

useEscToClose(lightboxOpen, () => { lightboxOpen.value = false })

// Offerte aanvraag
const quoteOpen    = ref(false)
const quoteModalEl = ref(null)
useEscToClose(quoteOpen, () => { quoteOpen.value = false })
useFocusTrap(quoteOpen, quoteModalEl)
const quoteDone      = ref(false)
const quoteSentEmail = ref('')   // bewaard e-mailadres voor de bevestigingstekst
const quoteLoading   = ref(false)
const quoteError     = ref('')
const quoteForm    = ref({ name: '', company: '', email: '', phone: '', quantity: '', message: '', website: '' })

// ── Computed ─────────────────────────────────────────────────────────────────
const product        = computed(() => productStore.currentProduct)
const loading        = computed(() => productStore.loading)

// Reset de hoofdfoto naar de eerste afbeelding zodra het product wisselt. Zonder
// dit bleef bij navigatie naar een ander product kort de vorige productfoto staan
// (currentImage werd alleen bij mount, ná de fetch, gezet).
watch(() => product.value?.id, (id, prev) => {
  if (id && id !== prev) {
    currentImage.value = product.value?.images?.[0]?.src || ''
    mainImgLoaded.value = false
  }
})
const productCategory = computed(() => {
  const cat = product.value?.categories?.find(c =>
    !c.slug?.includes('uncategor') && !c.name?.toLowerCase().includes('uncategor') && !c.name?.toLowerCase().includes('niet gecategoriseerd')
  )
  return cat?.name || null
})
const productCategorySlug = computed(() => {
  const cat = product.value?.categories?.find(c =>
    !c.slug?.includes('uncategor') && !c.name?.toLowerCase().includes('uncategor') && !c.name?.toLowerCase().includes('niet gecategoriseerd')
  )
  return cat?.slug || null
})
const isGrouped        = computed(() => product.value?.type === 'grouped')
const isBundle         = computed(() => product.value?.is_bundle === true)
const bundleItems      = ref([])   // [{ ...product, qty }] - qty uit bundle_items meta
const bundleRegular    = computed(() => bundleItems.value.reduce((sum, p) => sum + parseFloat(p.regular_price || p.price || 0) * (p.qty || 1), 0))
const bundleSetPrice   = computed(() => parseFloat(product.value?.price || 0))
const bundleSavings    = computed(() => Math.max(0, bundleRegular.value - bundleSetPrice.value))
const bundleSavingsPct = computed(() => bundleRegular.value > 0 ? Math.round((bundleSavings.value / bundleRegular.value) * 100) : 0)
const groupedTotal     = computed(() => groupedItems.value.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.qty), 0))
const groupedRegular   = computed(() => groupedItems.value.reduce((sum, item) => sum + (parseFloat(item.regular_price || item.price || 0) * item.qty), 0))
const groupedSavings   = computed(() => Math.max(0, groupedRegular.value - groupedTotal.value))
const groupedSavingsPct = computed(() => groupedRegular.value > 0 ? Math.round((groupedSavings.value / groupedRegular.value) * 100) : 0)
const currentPrice   = computed(() => product.value?.sale_price || product.value?.price || product.value?.regular_price)
const savingsAmount  = computed(() => {
  if (product.value?.on_sale && product.value.regular_price && currentPrice.value)
    return parseFloat(product.value.regular_price) - parseFloat(currentPrice.value)
  return 0
})
const savingsPercentage = computed(() => {
  if (savingsAmount.value > 0 && product.value?.regular_price)
    return Math.round((savingsAmount.value / parseFloat(product.value.regular_price)) * 100)
  return 0
})
const stockQuantity = computed(() => product.value?.stock_quantity || 0)
const lowStock      = computed(() => stockQuantity.value > 0 && stockQuantity.value <= 5)
const maxQuantity   = computed(() => product.value?.manage_stock ? (stockQuantity.value || 1) : 999)
const hasReviews    = computed(() => product.value?.rating_count > 0)
// Toon altijd sterren: gebruik echte beoordeling, of val terug op 4★
const displayRating = computed(() => {
  const r = parseFloat(product.value?.average_rating || 0)
  return r > 0 ? Math.round(r * 10) / 10 : 4
})
const addToCartText = computed(() => {
  if (adding.value)            return 'Toevoegen...'
  if (product.value?.stock_status === 'outofstock' && !product.value?.backorders_allowed) return 'Uitverkocht'
  return 'In winkelwagen'
})

// ── Product labels (WooCommerce product-tags) ─────────────────────────────────
const LABEL_TAGS = {
  'keuze-van-plug2charge': { text: 'Keuze van Plug2Charge', bg: '#FFD500', color: '#111', icon: 'bi-star-fill' },
  'bestseller':            { text: 'Bestseller',            bg: '#111',    color: '#fff', icon: 'bi-trophy-fill' },
  'populair':              { text: 'Populair',              bg: '#f97316', color: '#fff', icon: 'bi-fire' },
  'actie':                 { text: 'Actie',                 bg: '#ef4444', color: '#fff', icon: 'bi-tag-fill' },
  'limited':               { text: 'Limited',              bg: '#8b5cf6', color: '#fff', icon: 'bi-lightning-charge-fill' },
}
const productLabels = computed(() =>
  (product.value?.tags || [])
    .map(t => LABEL_TAGS[t.slug] ? { ...LABEL_TAGS[t.slug], slug: t.slug } : null)
    .filter(Boolean)
)

// ── Levertijden ───────────────────────────────────────────────────────────────
const deliveryTime = ref(null)   // { id, label, sub, color, icon } of null

const fetchDeliveryTime = async (productId) => {
  try {
    const res = await fetch(`/_delivery-time/${productId}`)
    if (res.ok) deliveryTime.value = await res.json()
  } catch (e) {
    console.warn('[delivery-time]', e.message)
  }
}

// ── Like / hart knop ──────────────────────────────────────────────────────────
const liked     = ref(false)
const likeCount = ref(0)  // wordt na hydration ingesteld vanuit localStorage

const toggleLike = () => {
  if (!product.value) return
  liked.value = !liked.value
  likeCount.value += liked.value ? 1 : -1
  try {
    const key = `p2c_liked_${product.value.id}`
    liked.value ? localStorage.setItem(key, '1') : localStorage.removeItem(key)
  } catch {}
}

// ── Magazijnlocaties ──────────────────────────────────────────────────────────
const warehouses     = ref([])   // alle locaties van /_warehouses
const warehouseStock = ref({})   // stock per locatie: { amsterdam: 5, rotterdam: 0 }
const warehouseItems = computed(() =>
  warehouses.value.map(wh => ({ ...wh, stock: warehouseStock.value[wh.id] ?? null }))
    .filter(wh => wh.stock !== null) // toon alleen als er meta-data voor is
)

const fetchWarehouseData = async (productId) => {
  try {
    const [whRes, stockRes] = await Promise.all([
      fetch('/_warehouses'),
      fetch(`/_warehouse-stock/${productId}`),
    ])
    if (whRes.ok)    warehouses.value    = await whRes.json()
    if (stockRes.ok) warehouseStock.value = await stockRes.json()
  } catch (e) {
    console.warn('[warehouse]', e.message)
  }
}

// ── BTW berekening (21%) ──────────────────────────────────────────────────────
const BTW_RATE = 0.21
const priceExclBtw = computed(() => {
  const p = parseFloat(currentPrice.value || 0)
  return p > 0 ? p / (1 + BTW_RATE) : 0
})
const vatAmount = computed(() => parseFloat(currentPrice.value || 0) - priceExclBtw.value)

// ── Betaalmethoden ────────────────────────────────────────────────────────────
const paymentMethods = [
  { key: 'ideal',      label: 'iDEAL'      },
  { key: 'bancontact', label: 'Bancontact' },
  { key: 'visa',       label: 'Visa'       },
  { key: 'mastercard', label: 'Mastercard' },
  { key: 'paypal',     label: 'PayPal'     },
  { key: 'klarna',     label: 'Klarna'     },
]

// ── Static data ───────────────────────────────────────────────────────────────
// ── Datasheets ────────────────────────────────────────────────────────────────
// Ondersteunt twee manieren om een datasheet in WooCommerce toe te voegen:
//   1. WC Downloads tab: product → Downloadbaar aanvinken → bestand uploaden
//   2. Custom meta veld met sleutel: datasheet, datasheet_url, _datasheet,
//      _datasheet_url, of _datasheet_file
const datasheets = computed(() => {
  if (!product.value) return []
  const sheets = []

  // Methode 1: WooCommerce Downloads
  for (const d of product.value.downloads || []) {
    if (d.file) sheets.push({ name: d.name || 'Datasheet', url: d.file })
  }

  // Methode 2: custom meta velden
  const DATASHEET_KEYS = ['datasheet', 'datasheet_url', '_datasheet', '_datasheet_url', '_datasheet_file']
  for (const m of product.value.meta_data || []) {
    if (DATASHEET_KEYS.includes(m.key) && m.value) {
      const exists = sheets.some(s => s.url === m.value)
      if (!exists) sheets.push({ name: 'Datasheet', url: m.value })
    }
  }

  return sheets
})

// Tabs zijn altijd een vaste array van 5 items zodat v-for nooit een
// structurele DOM-mismatch veroorzaakt tijdens SSR-hydration.
// De datasheets-tab verbergen we met v-show (display:none) in plaats van
// hem uit de array te laten - v-show is hydration-safe, v-if niet.
const tabs = computed(() => [
  { id: 'description', label: 'Beschrijving',      icon: 'bi-file-text',           show: !!(product.value?.description || product.value?.applications || product.value?.benefits?.length || product.value?.product_faq?.length) },
  { id: 'specs',       label: 'Specificaties',      icon: 'bi-list-ul',             show: true },
  { id: 'datasheets',  label: 'Datasheets',          icon: 'bi-file-earmark-pdf',    show: datasheets.value.length > 0 },
  { id: 'reviews',     label: 'Reviews',             icon: 'bi-star',
    badge: hasReviews.value ? product.value?.rating_count : null,                   show: true },
  { id: 'delivery',    label: 'Bezorging & Retour', icon: 'bi-truck',               show: true },
])

// Zet de actieve tab op de eerste zichtbare tab wanneer de huidige verborgen is
// (bv. product zonder omschrijving → open direct op Specificaties i.p.v. een lege tab).
watch(() => product.value?.slug, () => {
  const shown = tabs.value.filter(t => t.show)
  if (shown.length && !shown.some(t => t.id === activeTab.value)) {
    activeTab.value = shown[0].id
  }
}, { immediate: true })

const trustBadges = [
  { icon: 'bi-truck',         title: 'Gratis verzending', sub: 'Vanaf €50' },
  { icon: 'bi-shield-check',           title: '2 jaar garantie',   sub: 'Standaard op alles' },
  { icon: 'bi-arrow-clockwise',          title: '30 dagen retour',   sub: 'Risico vrij' },
  { icon: 'bi-lightning-charge',             title: 'Plug & Play',       sub: 'In 30 minuten klaar' },
]

const usps = [
  { icon: 'bi-lightning-charge',    title: 'Direct besparen',       desc: 'Lagere energierekening vanaf dag 1' },
  { icon: 'bi-hammer',  title: 'Eenvoudige installatie', desc: 'Zelf in 30 minuten klaar' },
  { icon: 'bi-sun',     title: 'Duurzaam',               desc: 'Tot 25 jaar meegaan' },
  { icon: 'bi-plug',    title: 'Plug & play',            desc: 'Gewoon in het stopcontact' },
]

// ── Methods ───────────────────────────────────────────────────────────────────
// Handmatige prijsopmaak in plaats van Intl.NumberFormat - voorkomt hydration
// mismatch: Node.js (small-icu / ICU v73) en browsers gebruiken verschillende
// Unicode-witruimte (U+00A0 vs U+202F) tussen het €-teken en het bedrag,
// waardoor Vue een tekst-node-mismatch detecteert en de component hermonteert.
// Die hermontage triggert page-fade-enter-from (opacity:0) → lege pagina.
const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '€ 0,00'
  const num = parseFloat(price)
  if (isNaN(num)) return '€ 0,00'
  const [intPart, decPart] = num.toFixed(2).split('.')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return '€ ' + intFormatted + ',' + decPart
}
const formatRating = (r) => r ? parseFloat(r).toFixed(1) : '0.0'
const formatReviewDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })
}
const increaseQuantity = () => { if (quantity.value < maxQuantity.value) quantity.value++ }
const decreaseQuantity = () => { if (quantity.value > 1) quantity.value-- }
const getRatingPercentage = (r) => ({ 5: 75, 4: 15, 3: 7, 2: 2, 1: 1 }[r] || 0)

const addToCart = async () => {
  if (adding.value || !product.value?.in_stock) return
  try {
    adding.value = true
    await cartStore.addToCart(product.value, quantity.value)
    trackAddToCart(product.value, quantity.value, 'product-detail')
    openCartModal(product.value, quantity.value)
    addedFeedback.value = true
    setTimeout(() => { addedFeedback.value = false }, 4000)
  } catch (err) {
    console.error('addToCart failed:', err)
  } finally {
    adding.value = false
  }
}

const addBundleToCart = async () => {
  if (adding.value) return
  try {
    adding.value = true
    const setName = product.value.name
    for (const item of bundleItems.value) {
      await cartStore.addToCart(item, item.qty || 1, null, setName)
    }
    // Track het bundle-product als geheel
    trackAddToCart(product.value, 1, 'bundle')
    openCartModal(product.value, 1)
    addedFeedback.value = true
    setTimeout(() => { addedFeedback.value = false }, 4000)
  } catch (err) {
    console.error('addBundleToCart failed:', err)
  } finally {
    adding.value = false
  }
}

const addGroupedToCart = async () => {
  const selected = groupedItems.value.filter(item => item.qty > 0)
  if (!selected.length || adding.value) return
  try {
    adding.value = true
    const setName = product.value.name
    for (const item of selected) {
      await cartStore.addToCart(item, item.qty, null, setName)
      trackAddToCart(item, item.qty, 'grouped')
    }
    openCartModal(product.value, selected.length)
    addedFeedback.value = true
    setTimeout(() => { addedFeedback.value = false }, 4000)
  } catch (err) {
    console.error('addGroupedToCart failed:', err)
  } finally {
    adding.value = false
  }
}

const quickAddToCart = async (p) => {
  try {
    await cartStore.addToCart(p, 1)
    trackAddToCart(p, 1, 'quick-add')
  } catch (e) { console.error(e) }
}

const fetchReviews = async (productId) => {
  try {
    reviewsLoading.value = true
    const data = await wooApi.getReviews(productId)
    reviews.value = Array.isArray(data) ? data : []
  } catch (e) {
    reviews.value = []
  } finally {
    reviewsLoading.value = false
  }
}

const fetchCrossSells = async (p) => {
  try {
    const ids = p.cross_sell_ids?.length
      ? p.cross_sell_ids.slice(0, 6)
      : p.related_ids?.slice(0, 6) || []
    if (!ids.length) return
    crossSells.value = await wooApi.getProductsByIds(ids)
  } catch (e) {
    crossSells.value = []
  }
}

const fetchUpsells = async (p) => {
  try {
    const ids = (p.upsell_ids || []).slice(0, 4)
    if (!ids.length) return
    upsells.value = await wooApi.getProductsByIds(ids)
  } catch (e) {
    upsells.value = []
  }
}

const initStickyBar = () => {
  if (!cartAreaRef.value || typeof IntersectionObserver === 'undefined') return
  stickyObserver = new IntersectionObserver(
    ([entry]) => { showStickyBar.value = !entry.isIntersecting },
    { threshold: 0 }
  )
  stickyObserver.observe(cartAreaRef.value)
}

const toggleWishlist = () => {
  if (!product.value) return
  const inWishlist = wishlistStore.isInWishlist(product.value.id)
  wishlistStore.toggleWishlist(product.value)
  trackWishlist(product.value, inWishlist ? 'remove' : 'add')
}

// ── Offerte aanvraag ──────────────────────────────────────────────────────────
const submitQuote = async () => {
  quoteError.value   = ''
  quoteLoading.value = true
  try {
    const res = await fetch('/api/quote-request', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...quoteForm.value,
        productId:   product.value?.id,
        productName: product.value?.name,
        productSlug: product.value?.slug,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Er ging iets mis')
    quoteSentEmail.value = quoteForm.value.email   // bewaren vóór de reset
    quoteDone.value = true
    trackQuoteRequest(product.value?.name || '')
    // Reset formulier
    quoteForm.value = { name: '', company: '', email: '', phone: '', quantity: '', message: '', website: '' }
  } catch (e) {
    quoteError.value = e.message || 'Er ging iets mis, probeer opnieuw.'
  } finally {
    quoteLoading.value = false
  }
}

const startDealTimer = () => {
  let h = 2, m = 34, s = 0
  dealInterval = setInterval(() => {
    if (s > 0) s--
    else if (m > 0) { m--; s = 59 }
    else if (h > 0) { h--; m = 59; s = 59 }
    else clearInterval(dealInterval)
    dealTimeLeft.value = `${h}u ${m}m ${String(s).padStart(2,'0')}s`
  }, 1000)
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  // Hoofdfoto kan al geladen zijn vóór hydratie → dan is @load al gemist.
  if (mainImgEl.value?.complete) mainImgLoaded.value = true
  // Sluit lightbox op Escape
  const onKey = (e) => { if (e.key === 'Escape') lightboxOpen.value = false }
  window.addEventListener('keydown', onKey)
  const cleanup = () => window.removeEventListener('keydown', onKey)
  // cleanup bij unmount - via onUnmounted hieronder
  window._lightboxCleanup = cleanup

  // Random viewCount instellen na hydration (SSR-safe: was 0 op server)
  viewCount.value = Math.floor(Math.random() * 20) + 5

  // Like-status & -teller ophalen uit localStorage na hydration
  if (product.value) {
    const key = `p2c_liked_${product.value.id}`
    liked.value = localStorage.getItem(key) === '1'
  }
  likeCount.value = Math.floor(Math.random() * 80) + 20

  // Als SSR het product al in de store heeft gezet (via SSR prefetch),
  // hoeven we niet opnieuw te fetchen - direct initialiseren.
  const alreadyLoaded = product.value?.slug === route.params.slug
  if (!alreadyLoaded) {
    await productStore.fetchProductBySlug(route.params.slug)
  }
  if (product.value) {
    currentImage.value = product.value.images?.[0]?.src || ''
    document.title = `${product.value.name} | Plug2Charge`
    if (product.value.on_sale) startDealTimer()
    trackViewItem(product.value)

    // Parallel ophalen: reviews + cross-sells + sticky bar + warehouse + grouped
    await nextTick()
    initStickyBar()
    fetchReviews(product.value.id)
    fetchCrossSells(product.value)
    fetchUpsells(product.value)
    fetchWarehouseData(product.value.id)
    fetchDeliveryTime(product.value.id)

    if (product.value.type === 'grouped' && product.value.grouped_products?.length) {
      try {
        const items = await wooApi.getProductsByIds(product.value.grouped_products)
        groupedItems.value = items.map(p => ({ ...p, qty: 1 }))
      } catch (e) {
        console.error('grouped products fetch failed:', e)
      }
    }

    // Bundle: haal bundle_items op (met aantallen) of val terug op upsell_ids
    if (product.value.is_bundle) {
      const itemDefs = product.value.bundle_items || []
      const ids = itemDefs.map(b => b.id).filter(Boolean)
      if (ids.length) {
        try {
          const products = await wooApi.getProductsByIds(ids)
          // Koppel qty uit bundle_items aan elk opgehaald product
          bundleItems.value = products.map(p => {
            const def = itemDefs.find(b => b.id === p.id)
            return { ...p, qty: def?.qty || 1 }
          })
        } catch (e) {
          console.error('bundle items fetch failed:', e)
        }
      }
    }
  }
})

onUnmounted(() => {
  if (dealInterval) clearInterval(dealInterval)
  if (stickyObserver) stickyObserver.disconnect()
  window._lightboxCleanup?.()
})
</script>

<style scoped>
/* Slide-down for feedback banner */
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.25s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-8px); }

/* Slide-up for sticky bar */
.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.28s ease, opacity 0.28s ease; }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(100%); opacity: 0; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

/* line-clamp utility */
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

/* Hide number input spinner */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
input[type=number] { -moz-appearance: textfield; }

/* prose-product: premium description styling */
.prose-product { color: #555; font-size: 0.9rem; line-height: 1.7; max-width: none; }
.prose-product :deep(h2) { font-size: 1.25rem; font-weight: 800; color: #111; margin-top: 1.5rem; margin-bottom: 0.5rem; }
.prose-product :deep(h3) { font-size: 1.05rem; font-weight: 700; color: #111; margin-top: 1.25rem; margin-bottom: 0.4rem; }
.prose-product :deep(p) { margin-bottom: 0.9rem; }
.prose-product :deep(ul) { list-style: none; padding: 0; margin-bottom: 1rem; }
.prose-product :deep(ul li) { display: flex; align-items: flex-start; gap: 0.5rem; margin-bottom: 0.4rem; }
.prose-product :deep(ul li::before) { content: ''; display: inline-block; width: 8px; height: 8px; background: #FFD500; border-radius: 50%; margin-top: 0.45em; flex-shrink: 0; }
.prose-product :deep(strong) { color: #333; font-weight: 700; }
.prose-product :deep(a) { color: #111; text-decoration: underline; text-underline-offset: 2px; }
</style>
